import React, { Dispatch, SetStateAction, FCWithChildren, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { createContext } from 'use-context-selector';
import useUser from '@hooks/user/use-user';
import useLocalizationQuery from '@hooks/localization/use-localization';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import {
  stateFieldRequiredCountries,
  EU_STATES,
  EU_COUNTRY_CODES,
  US_COUNTRY_CODE,
} from '@interfaces/models/seller-info/seller-extra-field-countries';
import { userIsMajor } from '@helpers/utils/date-utils';
import {
  generateValidationRules,
  validateDateFields,
  validateForm,
  validateState,
  getSelectedCountry,
  isStateFieldRequired,
  ValidationRules,
} from '@helpers/seller-info-helpers';
import useSellerTaxInformation from '@hooks/seller-information/use-seller-information-query';
import {
  ICountry,
  SellerInfoFormPayload,
  CountryTaxInformation,
  IState,
  SellerFormKeyType as FormKeyType,
  SellerFormValueType as FormValueType,
  IFlattenedSellerInformation,
} from '@interfaces/models/seller-info/seller-information';

type SellerInformationContextValues = {
  formInitValue: FormValueType;
  dateAndAgeErrors: { dateError: boolean; ageError: boolean };
  submitCount: number;
  selectedCountry: ICountry | null;
  stateIsRequired: boolean;
  taxInfo: CountryTaxInformation | null;
  showSuccess: boolean;
  payload: SellerInfoFormPayload | null;
  establishmentCountries: string[];
  error: boolean;
  userFlags: UserFlags;
  segment: string;
  updateStateAndTaxInfo(selectedCountry: ICountry, stateFieldRequiredCountries): void;
  updateUserForm(userData: IFlattenedSellerInformation, userFlags: UserFlags, user): FormValueType;
  updateFormAndErrors(userData: IFlattenedSellerInformation): void;
  saveSellerInformation(values: FormValueType): void;
  handleEstablishedSelectedCountries(establishmentCountries: ICountry[]): void;
  countries: ICountry[];
  initialErrorObject: FormKeyType;
  currentStates: IState[];
  validator: (values: FormValueType) => {};
  setShowSuccess: Dispatch<SetStateAction<boolean>>;
  setError: Dispatch<SetStateAction<boolean>>;
  setSubmitCount: Dispatch<SetStateAction<number>>;
  setOpenSellerInfoErrorModal: Dispatch<SetStateAction<boolean>>;
  openSellerInfoErrorModal: boolean;
};

export interface UserFlags {
  isUSB2CUser: boolean;
  isUSC2CUser: boolean;
  isEUB2CUser: boolean;
  isEUC2CUser: boolean;
  isUSUser: boolean;
}

const initialFormValues = {
  firstname: '',
  lastname: '',
  day: '',
  month: '',
  year: '',
  businessName: '',
  street: '',
  city: '',
  zip: '',
  country: '',
  state: '',
  tin: '',
  vat: '',
  countries: '',
  isElectronically: '',
};

const updateUserForm = (userData: IFlattenedSellerInformation, userFlags: UserFlags, user): FormValueType => {
  const updatedForm = { ...userData } as FormValueType;

  const updateField = (fieldKey, userDataKey, customValue = '') => {
    const fieldValue = userData[userDataKey] || customValue || '';
    updatedForm[fieldKey] = fieldValue;
  };

  updateField('firstname', 'firstName', userData?.firstName);
  updateField('lastname', 'lastName', userData?.lastName);
  updateField('firstname', 'firstname', userData?.firstName);
  updateField('lastname', 'lastname', userData?.lastName);
  updateField('city', 'city');
  updateField('zip', 'zipCode');
  updateField('street', 'street');
  updateField('country', 'country.iso2', userData?.country);
  updateField('state', 'state.iso', userData?.state);
  updateField('tin', 'tin', userData?.tin);

  if (userFlags.isUSB2CUser || userFlags.isEUB2CUser) {
    updateField('businessName', 'businessName', userData?.businessName);
  }

  if (userFlags.isEUB2CUser) {
    updateField('vat', 'vatNumber', userData?.vatNumber);
  }

  if (userFlags.isUSC2CUser || userFlags.isEUC2CUser) {
    if (userData?.birthDate || user?.birthDate) {
      const birthDate = new Date(userData?.birthDate || user?.birthDate);
      const year = String(birthDate?.getUTCFullYear()) ?? '0000';
      const month = String(birthDate?.getUTCMonth() + 1).padStart(2, '0') ?? '01';
      const day = String(birthDate?.getUTCDate()).padStart(2, '0') ?? '01';
      updateField('year', 'year', year);
      updateField('month', 'month', month);
      updateField('day', 'day', day);
    }
  }
  updateField(
    'isElectronically',
    'isElectronically',
    userData?.receive1099kElectronically === undefined || userData?.receive1099kElectronically === null
      ? ''
      : userData?.receive1099kElectronically.toString(),
  );

  return updatedForm;
};

const SellerInformationContext = createContext<SellerInformationContextValues>({} as SellerInformationContextValues);

const SellerInformationProvider: FCWithChildren = (props): React.JSX.Element => {
  const { children } = props;
  const { t } = useTranslation('get-paid');
  const { sendAnalyticEvent: sendSellerInfoAnalyticEvents } = useAnalyticEvents('seller_info');
  const { user } = useUser();
  const { countryList } = useLocalizationQuery();
  const [formInitValue, setFormInitValue] = useState<FormValueType>(initialFormValues);
  const [dateAndAgeErrors, setDateAndAgeErrors] = useState<{ dateError: boolean; ageError: boolean }>({
    dateError: false,
    ageError: false,
  });
  const [submitCount, setSubmitCount] = useState<number>(0);
  const [selectedCountry, setSelectedCountry] = useState<ICountry | null>(null);
  const [stateIsRequired, setStateIsRequired] = useState<boolean>(false);
  const [taxInfo, setTaxInfo] = useState<CountryTaxInformation | null>(null);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [payload, setPayload] = useState<SellerInfoFormPayload | null>(null);
  const [establishmentCountries, setEstablishmentCountries] = useState<string[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [openSellerInfoErrorModal, setOpenSellerInfoErrorModal] = useState<boolean>(false);
  const [currentStates, setCurrentStates] = useState<IState[]>([]);

  const [initialErrorObject, setInitialErrorObject] = useState<FormKeyType>({});
  const { billingAddress, taxInformation, updatedTaxInformation } = useSellerTaxInformation();

  const userFlags: UserFlags = useMemo(() => {
    const isB2CUser = user?.segment === 'B2C';
    const isC2CUser = user?.segment !== 'B2C'; // C2C and VIP consider as 'C2C'
    const isUSCountry = selectedCountry?.code === US_COUNTRY_CODE;

    return {
      isUSB2CUser: isB2CUser && isUSCountry,
      isUSC2CUser: isC2CUser && isUSCountry,
      isEUB2CUser: isB2CUser && !isUSCountry,
      isEUC2CUser: isC2CUser && !isUSCountry,
      isUSUser: isUSCountry,
    };
  }, [user?.segment, selectedCountry?.code]);

  const segment = useMemo(() => {
    return user?.segment === 'B2C' ? user?.segment : 'C2C';
  }, [user?.segment]);

  const userCountryStateData = useMemo(() => {
    if (countryList?.length) {
      const modifiedCountryList = countryList.map((item) => ({
        ...item,
        code: item.isoCode,
      }));

      const onlyEUAndUSCountries = modifiedCountryList.filter((country) => {
        return EU_COUNTRY_CODES.includes(country?.code) || country?.code === US_COUNTRY_CODE;
      });

      const initialStates = EU_STATES.reduce((result, countryStates) => {
        for (const country in countryStates) {
          result[country] = countryStates[country];
        }
        return result;
      }, {});

      return {
        countries: onlyEUAndUSCountries,
        states: initialStates,
      };
    }

    return {
      countries: [],
      states: {},
    };
  }, [countryList]);

  const countries = userCountryStateData?.countries || [];
  const states = userCountryStateData?.states || {};

  const updateStateAndTaxInfo = (selectedCountry: ICountry, stateFieldRequiredCountries): void => {
    const stateFieldIsRequired = isStateFieldRequired(selectedCountry?.code, stateFieldRequiredCountries);
    setStateIsRequired(stateFieldIsRequired);

    if (stateFieldIsRequired) {
      setCurrentStates(stateFieldIsRequired ? states?.[selectedCountry?.code?.toLowerCase()] || [] : []);
    } else {
      setCurrentStates([]);
    }

    if (selectedCountry?.code) {
      setTaxInfo({
        content: `${t(`GET_PAID.TAX_INFORMATION_FORM.TAX_DESCRIPTION.${segment}.${selectedCountry?.code}`)}`,
        title: `${t('GET_PAID.TAX_INFORMATION_FORM.TAX_NAME.GENERIC', 'Tax Identification Number')} (${t(
          `GET_PAID.TAX_INFORMATION_FORM.TAX_NAME.${segment}.${selectedCountry?.code}`,
          '',
        )})`,
      });
    } else {
      setTaxInfo({
        content: '',
        title: `${t(`GET_PAID.TAX_INFORMATION_FORM.TAX_NAME.GENERIC`, 'Tax Identification Number')}`,
      });
    }
  };

  const validationConfig: ValidationRules = generateValidationRules(userFlags);

  const fieldValidations = {
    firstname: validationConfig.firstname,
    lastname: validationConfig.lastname,
    country: validationConfig.country,
    state: validationConfig.state,
    street: validationConfig.street,
    city: validationConfig.city,
    zip: validationConfig.zip,
    vat: validationConfig.vat,
    businessName: validationConfig.businessName,
    day: validationConfig.day,
    month: validationConfig.month,
    tin: validationConfig.tin,
    year: validationConfig.year,
    countries: validationConfig.countries,
    isElectronically: validationConfig.isElectronically,
  };

  const validateFields = (values: FormValueType, fieldValidations): FormValueType => {
    const errors = {} as FormValueType;
    const fieldErrors = validateForm(values, fieldValidations);

    Object.assign(errors, fieldErrors);

    if (userFlags?.isUSC2CUser || userFlags?.isEUC2CUser) {
      if (values.day && values.month && values.year) {
        const dateErrors = validateDateFields(values, userIsMajor);
        setDateAndAgeErrors(dateErrors);
      }
    }

    if (values.country) {
      const selectedCountry = getSelectedCountry(countries, values.country);
      setSelectedCountry(selectedCountry);

      const stateFieldIsRequired = isStateFieldRequired(selectedCountry?.code, stateFieldRequiredCountries);
      Object.assign(errors, validateState(values, stateFieldIsRequired, t));
    }

    return errors;
  };

  const validator = useMemo(
    () => (values: FormValueType) => validateFields(values, fieldValidations),
    [fieldValidations],
  );

  const updateFormAndErrors = (userData: IFlattenedSellerInformation): void => {
    const updatedFormInitValue = updateUserForm(userData, userFlags, user);
    setFormInitValue(updatedFormInitValue);

    /**
     *  TODO: https://github.com/jaredpalmer/formik/issues/811
     * I could not find any built-in solutions at this point, I came up with this solution.
     */
    const errorObject = validator(updatedFormInitValue);
    const result = {};

    for (const key in updatedFormInitValue) {
      if (updatedFormInitValue[key]) {
        result[key] = !!errorObject[key];
      }
    }
    setInitialErrorObject(result);
  };

  useEffect(() => {
    if (!taxInformation && !billingAddress) {
      return;
    }

    if (!!user?.id) {
      updateFormAndErrors(taxInformation ? taxInformation : billingAddress);
      const selectedCountryToUse = taxInformation ? taxInformation?.country : billingAddress?.country;
      setSelectedCountry(getSelectedCountry(countries, selectedCountryToUse));
    }
  }, [billingAddress, user, taxInformation]);

  useEffect(() => {
    updateStateAndTaxInfo(selectedCountry, stateFieldRequiredCountries);
  }, [selectedCountry, stateFieldRequiredCountries]);

  const saveSellerInformation = async (values) => {
    try {
      const year = values?.year ?? '0000';
      const month = (values?.month ?? '01').padStart(2, '0');
      const day = (values?.day ?? '01').padStart(2, '0');
      const payload: SellerInfoFormPayload = {
        segment: segment,
        tin: values?.tin ?? '',
        address: {
          street: values?.street ?? '',
          city: values?.city ?? '',
          zipCode: values?.zip ?? '',
          country: values?.country ?? '',
          ...(values?.state && { state: values?.state ?? '' }),
          ...((userFlags.isEUB2CUser || userFlags.isUSB2CUser) && { establishmentCountries: establishmentCountries }),
        },
        ...(values?.vat && userFlags.isEUB2CUser && { vatNumber: values?.vat ?? '' }),
        ...((userFlags.isEUB2CUser || userFlags.isUSB2CUser) && {
          businessInformation: { name: values?.businessName ?? '' },
        }),
        ...((userFlags.isEUC2CUser || userFlags.isUSC2CUser) && {
          personalInformation: {
            firstName: values?.firstname ?? '',
            lastName: values?.lastname ?? '',
            birthDate: `${year}-${month}-${day}T00:00:00Z`,
          },
        }),
        ...(userFlags.isUSUser && {
          receive1099kElectronically: values?.isElectronically === 'true' ? true : false,
        }),
      };

      setPayload(payload);
      await updatedTaxInformation(payload);
      setOpenSellerInfoErrorModal(false);
      sendSellerInfoAnalyticEvents('seller_tax_submit_result', {
        label: 'submit_success',
      });
      setShowSuccess(true);
    } catch (axiosError) {
      const error = axiosError?.response?.data?.errors;
      const isAlreadySaved = error?.status === 400 && error?.detail?.[0]?.message === 'tax_information.already_saved';

      if (isAlreadySaved) {
        setShowSuccess(true);
        return;
      }

      sendSellerInfoAnalyticEvents('seller_tax_submit_result', {
        label: 'submit_error',
      });
      setSubmitCount(submitCount + 1);
      setOpenSellerInfoErrorModal(true);
    }
  };

  const handleEstablishedSelectedCountries = (establishmentCountries: ICountry[]): void => {
    const establishmentCountryCodes = establishmentCountries.map((country) => country?.code);
    setEstablishmentCountries(establishmentCountryCodes);
  };

  const sellerInformationContextValue = {
    formInitValue,
    dateAndAgeErrors,
    submitCount,
    selectedCountry,
    stateIsRequired,
    taxInfo,
    showSuccess,
    payload,
    establishmentCountries,
    error,
    userFlags,
    segment,
    updateStateAndTaxInfo,
    updateUserForm,
    updateFormAndErrors,
    saveSellerInformation,
    handleEstablishedSelectedCountries,
    countries,
    initialErrorObject,
    currentStates,
    validator,
    setShowSuccess,
    setSubmitCount,
    setError,
    openSellerInfoErrorModal,
    setOpenSellerInfoErrorModal,
  };

  return (
    <SellerInformationContext.Provider value={sellerInformationContextValue}>
      {children}
    </SellerInformationContext.Provider>
  );
};

export { SellerInformationProvider, SellerInformationContext };
